import React, { FC, useCallback, useState } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import { Box, Button, Flex } from '@rover/kibble/core';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import useIsDefaultLocale from '@rover/react-lib/src/pages/index/HomePage/hooks/useIsDefaultLocale';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag from '@rover/shared/js/hooks/useFlag';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';
import { ServiceType, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import { HomepageCtaClickEvent } from '../../utilities/analyticsEvents';

import HomePageSearchBoxLocationInput from './connectors/LocationInputConnector';
import ServiceTypeConnector from './connectors/ServiceTypeConnector';
import SearchBoxHiddenInputs from './SearchBoxHiddenInputs';
import type { UpdateFiltersFunc } from './UpdateFiltersFunc';

export interface Props {
  filters: HomepageSearchFilters;
  serviceType: ServiceType | undefined;
  serviceTypeOptions: ServiceTypeOption[];
  updateFilters: UpdateFiltersFunc;
  className?: string;
}

const ctaText = getBrandSpecificValue<MessageDescriptor>({
  rover: msg`Search`,
});

const getStartedCtaText = getBrandSpecificValue<MessageDescriptor>({
  rover: msg`Get Started`,
});

const SearchBox: FC<Props> = (props: Props) => {
  const { filters, serviceTypeOptions } = props;

  const { t, i18n } = useLingui();
  const { setScreenReaderAnnouncement } = useA11yContext();

  // Sticky interaction flag: once the user touches any field it stays true for
  // the component's lifetime, even if the field is later cleared.
  const [hasInteracted, setHasInteracted] = useState(false);
  const markInteraction = useCallback(() => setHasInteracted(true), []);

  const isInGeoBrowseExperiment = useFlag(FLAGS.GEO_BROWSE_HOMEPAGE_ENTRYPOINT);
  const { isAuthenticated } = useCurrentUserShim();
  // The geo-browse entrypoint is only translated for each country's default
  // locale, so restrict it to that locale and fall back to Search otherwise.
  const isDefaultLocale = useIsDefaultLocale();

  const isTraining = filters.serviceTypeSlug === ServiceTypeSlug.DOG_TRAINING;

  // Route unauthenticated, not-yet-interacted users to the /get-started/
  // interstitial when the geo browse gate is enabled.
  const action = (() => {
    if (isTraining) return '/training/search/';
    if (isInGeoBrowseExperiment && !isAuthenticated && !hasInteracted && isDefaultLocale) {
      return '/get-started/';
    }
    return '/search/';
  })();
  const routesToGetStarted = action === '/get-started/';

  // Native GET form submission: do NOT preventDefault — let the browser navigate.
  const handleSubmit = useCallback(() => {
    new HomepageCtaClickEvent().fireCrossPageEvent();
    setScreenReaderAnnouncement(t`Form submitted. Navigating to Search page.`);
  }, [setScreenReaderAnnouncement, t]);

  const areServicesLoaded = serviceTypeOptions?.length > 0;

  return (
    <Box
      className={props.className}
      background="primary"
      width={[768, 768, 850]}
      data-qa-id="homepage-search-box-container"
      data-testid="homepage-search-box-container"
      borderRadius="secondary"
    >
      <Box
        height="100%"
        p={['2x', '2x', '4x']}
        mt="0x"
        minHeight={areServicesLoaded ? undefined : '314px'}
      >
        {areServicesLoaded && (
          <>
            {/* ServiceTypeConnector and the location input live OUTSIDE the form so their
                interactive controls (e.g. SelectButton's value-less radios) are not submitted
                as stray params. The form carries state via SearchBoxHiddenInputs instead. */}
            <ServiceTypeConnector {...props} onInteract={markInteraction} />
            <Box>
              <Flex alignItems="flex-end" justifyContent="space-between" mb="3x">
                <Box flex="1" sx={{ maxWidth: '300px' }} mr="3x">
                  <HomePageSearchBoxLocationInput {...props} onInteract={markInteraction} />
                </Box>
                <Box flex="1" sx={{ maxWidth: '250px' }}>
                  <form method="GET" action={action} onSubmit={handleSubmit}>
                    {/* The Get Started interstitial should be reached with a clean URL, so
                        omit the hidden inputs that would otherwise be appended as query params. */}
                    {!routesToGetStarted && (
                      <SearchBoxHiddenInputs
                        filters={filters}
                        spacesRequired={1}
                        isDogTraining={isTraining}
                      />
                    )}
                    <Button
                      data-testid="search-box-submit"
                      size="default"
                      fullWidth
                      variant="primary"
                      webProps={{ type: 'submit' }}
                    >
                      {i18n._(routesToGetStarted ? getStartedCtaText : ctaText)}
                    </Button>
                  </form>
                </Box>
              </Flex>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SearchBox;
